import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import type { BulkReference } from '@/api/bulk';
import { Button, Card, EmptyState, LoadingState } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ReferenceCard } from './ReferenceCard';

export interface ReferenceListProps {
  references: BulkReference[];
  /** No page has arrived yet for any filter. */
  isPending: boolean;
  /** The first page failed and there is nothing to show. */
  isError: boolean;
  /** A new search, chip or sort is loading while the previous list stays. */
  isRefreshing: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  selectedId: string | null;
  onSelect: (reference: BulkReference) => void;
  onLoadMore: () => void;
  onRetry: () => void;
  onReset: () => void;
  className?: string;
}

/**
 * The reference cards as one radio group with its loading, error and empty
 * states and "Mehr laden" paging (R5, R9, R10).
 */
export function ReferenceList({
  references,
  isPending,
  isError,
  isRefreshing,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  selectedId,
  onSelect,
  onLoadMore,
  onRetry,
  onReset,
  className,
}: ReferenceListProps) {
  const { t } = useTranslation();
  const groupName = useId();

  if (isPending) {
    return (
      <LoadingState
        label={t('seller.bulk.reference.loading')}
        className={className}
      />
    );
  }

  if (isError) {
    return (
      <Card
        className={cn(
          'flex flex-col items-center gap-3 text-center',
          className
        )}
      >
        <p role="alert" className="text-sm text-danger">
          {t('seller.bulk.reference.loadFailed')}
        </p>
        <Button variant="outline" onClick={onRetry}>
          {t('seller.bulk.reference.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {isRefreshing && (
        <LoadingState
          label={t('seller.bulk.reference.loading')}
          className="py-3"
        />
      )}

      {references.length === 0 ? (
        !isRefreshing && (
          <EmptyState
            icon={<LayoutGrid className="h-8 w-8" />}
            title={t('seller.bulk.reference.empty.title')}
            description={t('seller.bulk.reference.empty.text')}
            action={
              <Button variant="outline" onClick={onReset}>
                {t('seller.bulk.reference.empty.reset')}
              </Button>
            }
          />
        )
      ) : (
        <div
          role="radiogroup"
          aria-label={t('seller.bulk.reference.title')}
          aria-busy={isRefreshing || undefined}
          className={cn(
            'flex flex-col gap-2.5 transition-opacity duration-[120ms]',
            isRefreshing && 'opacity-60'
          )}
        >
          {references.map((reference) => (
            <ReferenceCard
              key={reference.id}
              reference={reference}
              groupName={groupName}
              selected={reference.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}

      {hasNextPage && !isRefreshing && (
        <div className="mt-2 flex flex-col items-center gap-3">
          {isFetchNextPageError && (
            <p role="alert" className="text-sm text-danger">
              {t('seller.bulk.reference.loadFailed')}
            </p>
          )}
          <Button
            variant="outline"
            loading={isFetchingNextPage}
            loadingLabel={t('seller.bulk.reference.loading')}
            onClick={onLoadMore}
          >
            {isFetchNextPageError
              ? t('seller.bulk.reference.retry')
              : t('seller.bulk.reference.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
